import { MANUFACTURERS } from '../../../../utils/constants';
import type { GuidanceStep } from './GuidanceCard';

interface BuildRefinementGuidanceStepsParams {
    manufacturer: string | null;
    manufacturerLink: string | null;
    hasRdi: boolean;
    canRequestRevision: boolean;
}

/**
 * Step content for the refinement-design-approval guidance card. Kept apart
 * from GuidanceCard's layout so a future page can supply its own steps.
 */
export function buildRefinementGuidanceSteps({
    manufacturer,
    manufacturerLink,
    hasRdi,
    canRequestRevision
}: BuildRefinementGuidanceStepsParams): GuidanceStep[] {
    // Trailing "Aligners" reads awkwardly mid-sentence ("Open the case in Spark").
    const manufacturerName =
        MANUFACTURERS.find((m) => m.id === manufacturer)?.name.replace(/\s*Aligners$/i, '') ?? null;
    const openTarget = manufacturerName
        ? `Open the case in ${manufacturerName}.`
        : 'Open the case with the manufacturer.';

    const steps: GuidanceStep[] = [
        {
            id: 'open',
            content: manufacturerLink ? (
                <>{openTarget} The link is in the patient details above.</>
            ) : (
                openTarget
            )
        },
        { id: 'review-design', content: 'Review the latest refinement design.' }
    ];

    if (hasRdi) {
        steps.push({ id: 'review-rdi', content: 'Review the RDI in the panel on the left.' });
    }

    steps.push({
        id: 'approve',
        content: (
            <>
                If the design meets your treatment goals, click <strong>Approve design</strong> to
                send it to manufacturing.
            </>
        )
    });

    if (canRequestRevision) {
        steps.push({
            id: 'request-revision',
            content: (
                <>
                    If the design needs clinical changes, open the ⋮ menu and choose{' '}
                    <strong>Request a revision</strong>. This returns the case to the design team
                    for a new design cycle.
                </>
            )
        });
    }

    return steps;
}
